import { useEffect, useRef, useState } from "react";

/**
 * Vibe — the Anam AI video avatar.
 *
 * Anam authenticates the widget by origin: the agent id below is public, and
 * the allowed origins are configured in Anam Lab. There is no client-side API
 * key to pass, and none should ever be added here.
 *
 * Two things this component is careful about:
 *
 * 1. The script is pinned. It used to load `https://unpkg.com/@anam-ai/agent-widget`
 *    unversioned, so any breaking release upstream would change behaviour on the
 *    live site with no deploy on our side.
 *
 * 2. The element is mounted once and hidden with CSS, never unmounted. App.js
 *    previously dropped it whenever the route was an admin or lender page, and
 *    re-created it on the way back. Tearing down a live WebRTC session that way
 *    can leave the microphone track open and burns avatar minutes.
 */

const AGENT_ID = process.env.REACT_APP_ANAM_AGENT_ID || "9a8ede50-592e-48bb-95fd-19bce81cbaa8";
const WIDGET_VERSION = "0.6.1";
const WIDGET_SRC = `https://unpkg.com/@anam-ai/agent-widget@${WIDGET_VERSION}/dist/index.umd.js`;
const LOAD_TIMEOUT_MS = 12000;

// Module-level so React 18's double-invoked effects, and any remount, reuse the
// same in-flight load instead of injecting a second <script>. Defining the same
// custom element twice throws "anam-agent has already been defined".
let widgetLoad = null;

function loadAnamWidget() {
    if (typeof window === "undefined") return Promise.reject(new Error("no window"));
    if (window.customElements?.get("anam-agent")) return Promise.resolve();
    if (widgetLoad) return widgetLoad;

    widgetLoad = new Promise((resolve, reject) => {
        let script = document.querySelector('script[data-anam-widget]');
        if (!script) {
            script = document.createElement("script");
            script.src = WIDGET_SRC;
            script.async = true;
            script.crossOrigin = "anonymous";
            script.setAttribute("data-anam-widget", "true");
            document.head.appendChild(script);
        }
        const timer = setTimeout(() => reject(new Error("anam widget load timed out")), LOAD_TIMEOUT_MS);
        script.addEventListener("load", () => { clearTimeout(timer); resolve(); }, { once: true });
        script.addEventListener("error", () => { clearTimeout(timer); reject(new Error("anam widget failed to load")); }, { once: true });
    }).catch((err) => {
        widgetLoad = null; // allow a later retry
        throw err;
    });

    return widgetLoad;
}

export default function VibeChat({ hidden = false }) {
    const containerRef = useRef(null);
    const [failed, setFailed] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || container.querySelector("anam-agent")) return;

        let cancelled = false;
        loadAnamWidget()
            .then(() => {
                if (cancelled || !containerRef.current) return;
                if (containerRef.current.querySelector("anam-agent")) return;
                // createElement + setAttribute because React does not reliably
                // set hyphenated attributes on custom elements.
                const el = document.createElement("anam-agent");
                el.setAttribute("agent-id", AGENT_ID);
                el.addEventListener("anam-agent:error", () => setFailed(true));
                containerRef.current.appendChild(el);
            })
            .catch(() => { if (!cancelled) setFailed(true); });

        return () => { cancelled = true; };
    }, []);

    return (
        <>
            <div
                ref={containerRef}
                className={`vibe-widget fixed bottom-0 right-0 z-40 isolate ${hidden ? "invisible pointer-events-none" : ""}`}
                aria-hidden={hidden || undefined}
                data-testid="vibe-avatar"
            />
            {failed && !dismissed && !hidden && (
                <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    className="fixed bottom-4 right-4 z-40 text-xs px-3 py-2 rounded-full bg-elevated/90 border border-white/10 text-text-secondary hover:text-text-primary backdrop-blur"
                    data-testid="vibe-unavailable"
                >
                    Vibe is offline right now · dismiss
                </button>
            )}
        </>
    );
}
